import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Button, Typography, Link as MuiLink } from '@mui/material';
import { changeApplicationStatus, getFetchingApplicationsData, resetApplicationStatusChangeStates, selectApplicationsState } from '../sagas/pm/applicationsSlice';
import CheckMarkIcon from '../../../icons/CheckMark.svg';
import IncompleteIcon from '../../../icons/IncompleteIcon.svg';
import {
  CustomQuestionTypeEnum,
  PaymentTypeEnum,
  CreateOneTimePaymentDto,
  TransactionDTO,
  ApplicationTsCsDto,
  CreateTransactionDetailDto,
  ChangeApplicationStatusEnum,
  UpdateApplicantStatusEnum,
  UpdateApplicantStatusDto,
} from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { WizardPageStates } from './ApplicationWizard';
import { applicationStyles } from './CustomStyles';
import { clearPayModalReduxStates, getPaymentFeesByResident, getTransactionHistory, makeOneTimePayment, selectPaymentState } from '../../payments/sagas/residentpaymentsSlice';
import { formatAsCurrency } from '../../../shared/util/formaters';
import { format } from 'date-fns';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import ApplicationPaymentModal from './ApplicationPaymentModal';
import ApplicationReviewModal from './ApplicationReviewModal';
import ConfirmPayModal from './ConfirmPayModal';
import ApplicationPaymentSuccessModal from './ApplicationPaymentSuccessModal';
import { useHistory } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  AdditionalQuestionsValidator,
  ApplicantPetValidator,
  ApplicantVehicleValidator,
  ApplicationConsentEntity,
  CoapplicantValidator,
  EmploymentIncomeValidator,
  MinorOccupantValidator,
  PersonalInfoValidator,
  PropertyInfoValidator,
  ReleasesConsentsValidator,
  RentalHistoryValidator,
} from '../validators/ApplicantValidators';
import {
  getAllRentalHistoryRecords,
  getApplicantAllPetRecords,
  getApplicantAllVehicleRecords,
  getApplicantV2,
  getApplicationAcceptedTCsV2,
  getApplicationV2,
  getEmploymentIncomeRecords,
  getMinorRecords,
  selectApplicantsState,
  updateApplicantStatus,
} from '../sagas/applicants/applicantsSlice';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import CoapplicantSubmitModal from './CoapplicantSubmitModal';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { DEFAULT_ADHOC_BANK_UUID, DEFAULT_ADHOC_CARD_UUID, selectAdhocBankAccount, selectAdhocBankCard } from '../../../pmui/residents/saga/pmResidentsSlice';
import ConfirmAppSubmissionModal from './ConfirmAppSubmissionModal';

interface ApplicationReviewPropsType {
  appUuid: string;
  PersonalInfoValidator: PersonalInfoValidator;
  EmployeeValidator: EmploymentIncomeValidator;
  MinorOccupantValidator: MinorOccupantValidator;
  AdditionalQuestionsValidator: AdditionalQuestionsValidator;
  CoapplicantValidator: CoapplicantValidator;
  PropertyInfoValidator: PropertyInfoValidator;
  ReleasesConsentsValidator: ReleasesConsentsValidator;
  RentalHistoryValidator: RentalHistoryValidator;
  PetsValidator: ApplicantPetValidator;
  VehiclesValidator: ApplicantVehicleValidator;
}

export default function ApplicationReview(props: ApplicationReviewPropsType) {
  const dispatch = useDispatch();
  const { ipaUser } = useSelector(selectAuthState);
  const classes = applicationStyles();
  const history = useHistory();

  //selectors
  const { PaymentMadeDetails, isMakeOtpSuccess, isMakeOtpError, makeOneTimePaymentError } = useSelector(selectPaymentState);
  const adhocBankAccount = useSelector(selectAdhocBankAccount);
  const adhocBankCard = useSelector(selectAdhocBankCard);
  const { isChangingApplicationStatusSuccess, isChangingApplicationStatusFailed } = useSelector(selectApplicationsState);

  const {
    Application,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    Applicant,
    isFetchingApplicantSuccess,
    EmploymentIncomeRecords,
    isFetchingEmploymentIncomeRecordsSuccess,
    isFetchingEmploymentIncomeRecordsFailed,
    isFetchingMinorRecordsSuccess,
    MinorRecords,
    isGettingApplicationAcceptedTCsSuccess,
    isGettingApplicationAcceptedTCsFailed,
    applicationAcceptedTCs,
    organizationApplicationTCs, // these are company terms that need to be accepted
    isFetchingOrganizationApplicationTCsSuccess,
    isFetchingOrganizationApplicationTCsFailed,
    allRentalHistoryRecords,
    isFetchingAllRentalHistoryRecordsSuccess,
    isFetchingAllRentalHistoryRecordsFailed,
    applicantAllPetRecords,
    isFetchingApplicantAllPetRecordsSuccess,
    isFetchingApplicantAllPetRecordsFailed,
    applicantAllVehicleRecords,
    isFetchingApplicantAllVehicleRecordsSuccess,
    isFetchingApplicantAllVehicleRecordsFailed,
    isUpdatingApplicantStatusSuccess,
    updateApplicantStatusResponse,
    isUpdatingApplicantStatusFailed,
  } = useSelector(selectApplicantsState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  //local state
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState<string | null>(null);
  const [showMinor, setShowMinor] = useState(true);
  const [showPet, setShowPet] = useState(true);
  const [showVehicle, setShowVehicle] = useState(true);
  const [showAdditionalQuestions, setShowAdditionalQuestions] = useState(true);
  const [isCoapplicantConfirmModalOpen, setIsCoapplicantConfirmModalOpen] = useState(false);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);

  //completed or not
  const [personalInfoCompleted, setPersonalInfoCompleted] = useState(false);
  const [employmentCompleted, setEmploymentCompleted] = useState(false);
  const [rentalHistoryCompleted, setRentalHistoryCompleted] = useState(false);
  const [petCompleted, setPetCompleted] = useState(false);
  const [coapplicantCompleted, setCoapplicantCompleted] = useState(false);
  const [minorsCompleted, setMinorsCompleted] = useState(false);
  const [vehicleCompleted, setVehicleCompleted] = useState(false);
  const [propertyInfoCompleted, setPropertyInfoCompleted] = useState(false);
  const [consentsCompleted, setConsentsCompleted] = useState(false);
  const [additionalQuestionCompleted, setAdditionalQuestionCompleted] = useState(false);
  const [isSubmittable, setIsSubmittable] = useState(false); //flag indicating the applicant/coapplicant can submit (all requirements have been met)
  const [submitButtonClicked, setSubmitButtonClicked] = useState(false); //flag indicating that 'submit' was clicked, we need it bcz in some scenarios we wanna open the submit modal only when it is clicked

  //financials
  const [moneyToSubmit, setMoneyToSubmit] = useState(0);
  const [isOpenAppPayModal, setIsOpenAppPayModal] = useState(false);
  const [payMethodUuid, setPayMethodUuid] = useState('');
  const [paymentFees, setPaymentFees] = useState(0);
  const [last4, setLast4] = useState('');
  const [isOpenAppReviewModal, setIsOpenAppReviewModal] = useState(false);
  const [isOpenAppConfirmModal, setIsOpenAppConfirmModal] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<TransactionDTO | null>(null);
  const [isOpenAppPaymentSuccessModal, setIsOpenAppPaymentSuccessModal] = useState(false);
  const [isCurrentUserThePrimaryApplicant, setIsCurrentUserThePrimaryApplicant] = useState(true);
  const [alreadyPaidAmount, setAlreadyPaidAmount] = useState(0); //already paid amount on this app
  const [isOpenAppConfirmSubModal, setIsOpenAppConfirmSubModal] = useState(false);

  //v2 useEffect. Fetch all the goods
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      dispatch(getEmploymentIncomeRecords(ipaUser?.currentProfile.uuid));
      dispatch({ type: getApplicantV2.type });
      dispatch(getMinorRecords(ipaUser?.currentProfile.uuid));
      //for co-applicants, no need to fetch since the Wizard page already fetched it above us.

      //fetch the application to get property info
      dispatch({
        type: getApplicationV2.type,
        payload: props.appUuid,
      });

      //get accepted TCs from the JSON
      dispatch({
        type: getApplicationAcceptedTCsV2.type,
        payload: { applicationUuid: props.appUuid, applicantUuid: ipaUser?.currentProfile.uuid },
      });

      //get all rental history records
      dispatch({
        type: getAllRentalHistoryRecords.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
        },
      });

      //get all pets
      dispatch({
        type: getApplicantAllPetRecords.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
        },
      });

      //get all vehicles
      dispatch({
        type: getApplicantAllVehicleRecords.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
        },
      });
    }
  }, [dispatch, ipaUser]);

  useEffect(() => {
    if (isFetchingApplicationSuccess) {
      let isPrimaryApplicant = ipaUser?.currentProfile.uuid === Application?.PrimaryApplicant.Uuid;
      setIsCurrentUserThePrimaryApplicant(isPrimaryApplicant);
      if (isPrimaryApplicant) {
        const areAllCoapplicantsComplete = Application.CoApplicants.every((ca) => {
          return props.CoapplicantValidator.IsEntireObjectComplete(ca);
        });
        setCoapplicantCompleted(areAllCoapplicantsComplete);
      }
    }
  }, [Application, isFetchingApplicationSuccess]);

  //deal with property Info
  useEffect(() => {
    if (isFetchingApplicationSuccess && isFetchingPropertyGroupDetailsSuccess) {
      const isPropertySectionComplete = props.PropertyInfoValidator.IsEntireObjectComplete(Application);
      setPropertyInfoCompleted(isPropertySectionComplete);
    }
  }, [Application, isFetchingApplicationSuccess, isFetchingPropertyGroupDetailsSuccess]);

  //deal with Personal Info
  useEffect(() => {
    if (isFetchingApplicantSuccess && isFetchingPropertyGroupDetailsSuccess) {
      const isPersonalSectionComplete = props.PersonalInfoValidator.IsEntireObjectComplete(Applicant);
      setPersonalInfoCompleted(isPersonalSectionComplete);

      const isAdditionalQuestionsComplete = props.AdditionalQuestionsValidator.IsEntireObjectComplete(Applicant);
      setAdditionalQuestionCompleted(isAdditionalQuestionsComplete);
    }
  }, [Applicant, isFetchingApplicantSuccess, isFetchingPropertyGroupDetailsSuccess]);

  //Deal with Employment info
  useEffect(() => {
    if (isFetchingEmploymentIncomeRecordsSuccess && isFetchingPropertyGroupDetailsSuccess) {
      const areAllEmploymentsGood = EmploymentIncomeRecords.every((e) => {
        const isPersonalSectionComplete = props.EmployeeValidator.IsEntireObjectComplete(e);
        return isPersonalSectionComplete;
      });
      setEmploymentCompleted(areAllEmploymentsGood && !!EmploymentIncomeRecords?.length);
    }
  }, [EmploymentIncomeRecords, isFetchingEmploymentIncomeRecordsSuccess, isFetchingPropertyGroupDetailsSuccess]);

  //Deal with Rental info
  useEffect(() => {
    if (isFetchingAllRentalHistoryRecordsSuccess && isFetchingPropertyGroupDetailsSuccess) {
      let currentAddresses: string[] = [];
      const areAllRentalsGood = allRentalHistoryRecords.every((r) => {
        if (r.IsCurrentAddress) currentAddresses.push(r.Uuid);
        const isRentalHistorySectionComplete = props.RentalHistoryValidator.IsEntireObjectComplete(r);
        return isRentalHistorySectionComplete;
      });

      //we should have exactly one current address
      if (currentAddresses.length > 1) setLocalError('Error: Exactly one current address is required.');

      setRentalHistoryCompleted(areAllRentalsGood && !!allRentalHistoryRecords?.length && currentAddresses.length === 1);
    }
  }, [allRentalHistoryRecords, isFetchingAllRentalHistoryRecordsSuccess, isFetchingPropertyGroupDetailsSuccess]);

  //deal with Minors
  useEffect(() => {
    if (isFetchingMinorRecordsSuccess && isFetchingPropertyGroupDetailsSuccess && isFetchingApplicantSuccess) {
      const areAllMinorsGood = MinorRecords.every((m) => {
        const isMinorComplete = props.MinorOccupantValidator.IsEntireObjectComplete(m);
        return isMinorComplete;
      });

      setMinorsCompleted((areAllMinorsGood && !!MinorRecords?.length) || Boolean(Applicant.HasNoMinors));
    }
  }, [MinorRecords, Applicant, isFetchingApplicantSuccess, isFetchingMinorRecordsSuccess, isFetchingPropertyGroupDetailsSuccess]);

  //deal with pets
  useEffect(() => {
    if (isFetchingApplicantAllPetRecordsSuccess && isFetchingPropertyGroupDetailsSuccess && isFetchingApplicantSuccess) {
      const areAllPetsGood = applicantAllPetRecords.every((p) => {
        const isPetComplete = props.PetsValidator.IsEntireObjectComplete(p);
        return isPetComplete;
      });
      setPetCompleted((areAllPetsGood && !!applicantAllPetRecords.length) || Boolean(Applicant.HasNoAnimals));
    }
  }, [applicantAllPetRecords, isFetchingApplicantAllPetRecordsSuccess, Applicant, isFetchingPropertyGroupDetailsSuccess]);

  //deal with Vehicles
  useEffect(() => {
    if (isFetchingApplicantAllVehicleRecordsSuccess && isFetchingPropertyGroupDetailsSuccess && isFetchingApplicantSuccess) {
      const areAllVehiclesGood = applicantAllVehicleRecords.every((v) => {
        const isVehicleComplete = props.VehiclesValidator.IsEntireObjectComplete(v);
        return isVehicleComplete;
      });
      setVehicleCompleted((areAllVehiclesGood && !!applicantAllVehicleRecords.length) || Boolean(Applicant.HasNoVehicles));
    }
  }, [applicantAllVehicleRecords, isFetchingApplicantAllVehicleRecordsSuccess, Applicant, isFetchingPropertyGroupDetailsSuccess]);

  //deal with releases and consents
  useEffect(() => {
    if (isGettingApplicationAcceptedTCsSuccess && isFetchingOrganizationApplicationTCsSuccess) {
      const areAllConsentsGood = Object.keys(organizationApplicationTCs).every((consentsObjectKey) => {
        const consentObjToCheck: ApplicationConsentEntity = {
          Uuid: ipaUser?.currentProfile.uuid!,
          consentToCheck: organizationApplicationTCs[consentsObjectKey as keyof ApplicationTsCsDto],
          acceptedTsCsAudit: applicationAcceptedTCs,
        };
        const isConsentComplete = props.ReleasesConsentsValidator.IsEntireObjectComplete(consentObjToCheck);
        return isConsentComplete;
      });

      setConsentsCompleted(areAllConsentsGood && !!applicationAcceptedTCs?.length);
    }
  }, [isGettingApplicationAcceptedTCsSuccess, isFetchingOrganizationApplicationTCsSuccess, organizationApplicationTCs]);

  //apply property group configs for section navigation
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess) {
      setShowPet(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowPets);
      setShowVehicle(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowVehicles);
      setShowMinor(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowAdditionalOccupants);
      const organizationCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.CustomV1,
      );
      setShowAdditionalQuestions(!!organizationCustomQuestions?.length && !!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsEnableCustomQuestionsSection);
    }
  }, [isFetchingPropertyGroupDetailsSuccess]);
  //end v2 useEffects

  useEffect(() => {
    //payment amount check
    if (Application && isFetchingPropertyGroupDetailsSuccess) {
      const ApplicationsConfigs = propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs;
      if (isCurrentUserThePrimaryApplicant) {
        const appFee = Application.ApplicationFee?.Total || 0;
        //get all already paid amounts on this app that are not inosio fees
        const alreadyPaidAmounts = Application.PaymentDetails?.flatMap((trans) => trans.TransactionDetails.filter((transDetail) => transDetail.EntityPaymentTypeId !== 1).map((td) => td.Amount)) || [];
        // Sum the amounts
        const totalPaidAmount = alreadyPaidAmounts.reduce((acc, amount) => acc + (amount || 0), 0);

        setAlreadyPaidAmount(totalPaidAmount);

        let moneyToSubmit = 0;

        //if the money to submit is less than 0, assign it zero
        if (appFee - totalPaidAmount > 0) moneyToSubmit = appFee - totalPaidAmount;

        setMoneyToSubmit(moneyToSubmit);

        ApplicationsConfigs?.ApplicationsEntityPaymentTypeId &&
          dispatch({
            type: getPaymentFeesByResident.type,
            payload: {
              moneyToSubmit,
              propPayTypeId: ApplicationsConfigs?.ApplicationsEntityPaymentTypeId,
            },
          });
      }
    }
  }, [Application, isCurrentUserThePrimaryApplicant, ipaUser, propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess]);

  //deal with payment result. if successful, change the application status
  useEffect(() => {
    if (isMakeOtpSuccess) {
      dispatch({
        type: changeApplicationStatus.type,
        payload: {
          applicationUuid: Application?.Uuid,
          changeAppStatusDto: {
            Status: ChangeApplicationStatusEnum.Submit,
          },
        },
      });
      setGlobalError(null);
      setTransaction(PaymentMadeDetails);
    } else if (isMakeOtpError) {
      setIsSpinnerModalOpen(false);
      setGlobalError(makeOneTimePaymentError);
      setIsOpenAppReviewModal(true);
    }
  }, [isMakeOtpSuccess, isMakeOtpError]);

  //deal with app submission result
  useEffect(() => {
    //when the app is submitted without any payment
    if (isChangingApplicationStatusSuccess && moneyToSubmit === 0) {
      setGlobalError(null);
      setSpinnerStatus('stop');
      setIsOpenAppConfirmSubModal(false);
      dispatch(
        //! is this necessary? won't the other page fetch it on load?
        getFetchingApplicationsData({
          RangeSize: 100,
          StartIndex: 0,
        }),
      );
      dispatch(getTransactionHistory());
      history.push(`/resui/applications`);
      dispatch({ type: clearPayModalReduxStates.type }); //clear out the payment states for this app
      dispatch({ type: resetApplicationStatusChangeStates.type }); //clear out the app status change states for this app
    } else if (isChangingApplicationStatusFailed && moneyToSubmit === 0) {
      setIsSpinnerModalOpen(false);
      setGlobalError('Something went wrong while submitting application, please try again later or contact support');
    }

    //when the app is submitted with payment
    else if (isChangingApplicationStatusSuccess && moneyToSubmit !== 0) {
      setIsOpenAppPaymentSuccessModal(true);
    } else if (isChangingApplicationStatusFailed && moneyToSubmit !== 0) {
      setIsSpinnerModalOpen(false);
      setGlobalError('Your payment was successful, but we encountered an issue with your application submission. Please contact support for assistance.');
      setIsOpenAppReviewModal(true);
      dispatch({
        type: getApplicationV2.type,
        payload: props.appUuid,
      });
    }
  }, [isChangingApplicationStatusSuccess, isChangingApplicationStatusFailed]);

  //determine if app is submittable
  useEffect(() => {
    /**
     * Algorithm is as follows.
     * If current applicant the primary? if no to any of these, then no. Yes otherwise.
     *  - is personal info submitted?
     *  - is employment info submitted?
     *  - is rental history submitted?
     *  - if showMinor, then are minors completed?
     *  - if showPet, then are pets completed?
     *  - if showVehicle, then are vehicles completed?
     *  - if showAdditionalQuestions, are additionals questions completed?
     *  - are consents submitted?
     *  - Are all co-applicants submitted? P
     *  - is propertyInfo submitted? P
     *
     *
     * if current applicant is a coapplicant?
     *  - is personal info submitted?
     *  - is employment info submitted?
     *  - is rental history submitted?
     *  - if showMinor, then are minors completed?
     *  - if showPet, then are pets completed?
     *  - if showVehicle, then are vehicles completed?
     *  - if showAdditionalQuestions, are additionals questions completed?
     *  - are consents submitted?
     *
     *
     *
     */

    let answer = false;

    if (
      personalInfoCompleted &&
      employmentCompleted &&
      rentalHistoryCompleted &&
      (!showMinor || minorsCompleted) &&
      (!showPet || petCompleted) &&
      (!showVehicle || vehicleCompleted) &&
      (!showAdditionalQuestions || additionalQuestionCompleted) &&
      consentsCompleted &&
      (!isCurrentUserThePrimaryApplicant || (coapplicantCompleted && propertyInfoCompleted))
    ) {
      answer = true;
    }
    setIsSubmittable(answer);
  }, [
    personalInfoCompleted,
    employmentCompleted,
    rentalHistoryCompleted,
    coapplicantCompleted,
    minorsCompleted,
    petCompleted,
    vehicleCompleted,
    additionalQuestionCompleted,
    propertyInfoCompleted,
    consentsCompleted,
    showMinor,
    showPet,
    showVehicle,
    showAdditionalQuestions,
    isCurrentUserThePrimaryApplicant,
  ]);

  /**
   * When the applicant lands on the review page, change their status to incomplete if all required fields are not complete
   * and the applicant is not already in 'incomplete' status. Also do this only when the app is incomplete or returned.
   */
  useEffect(() => {
    if (!isSubmittable && ['INCOMPLETE', 'RETURNED'].includes(Application.Status) && Applicant?.Status !== 'REGISTERED') {
      dispatch(
        updateApplicantStatus({
          applicationUuid: Application?.Uuid,
          applicantUuid: Applicant?.Uuid,
          updateApplicantStatusDto: { Status: UpdateApplicantStatusEnum.Incomplete } as UpdateApplicantStatusDto,
        }),
      );
    }
  }, [isSubmittable]);

  //handle failure/success of update applicant status
  useEffect(() => {
    setIsSpinnerModalOpen(false);
    //do the below only when submit button is clicked, not every time we land on this page
    if (isUpdatingApplicantStatusSuccess && updateApplicantStatusResponse?.Status === 'SUBMITTED' && !isCurrentUserThePrimaryApplicant && submitButtonClicked) {
      /**Only do this for the coapplicant flow.
       * For primary applicants, they still need to pay and then submit the app or submit only if app fee didnt change */
      history.push(`/resui/applications`);
      return;
    } else if (isUpdatingApplicantStatusSuccess && updateApplicantStatusResponse?.Status === 'SUBMITTED' && isCurrentUserThePrimaryApplicant && submitButtonClicked) {
      if (moneyToSubmit === 0) {
        setIsOpenAppConfirmSubModal(true);
      } else {
        dispatch({ type: clearPayModalReduxStates.type });
        setIsOpenAppPayModal(true);
      }
    } else if (isUpdatingApplicantStatusFailed) {
      //display error
    }
  }, [isUpdatingApplicantStatusSuccess, isUpdatingApplicantStatusFailed, updateApplicantStatusResponse]);

  useEffect(() => {
    if (
      isFetchingApplicationSuccess &&
      isFetchingPropertyGroupDetailsSuccess &&
      isFetchingAllRentalHistoryRecordsSuccess &&
      isFetchingEmploymentIncomeRecordsSuccess &&
      isFetchingApplicantAllVehicleRecordsSuccess &&
      isFetchingApplicantAllPetRecordsSuccess &&
      isFetchingOrganizationApplicationTCsSuccess &&
      isGettingApplicationAcceptedTCsSuccess
    ) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (
      isFetchingApplicationFailure ||
      isFetchingPropertyGroupDetailsFailed ||
      isFetchingAllRentalHistoryRecordsFailed ||
      isFetchingEmploymentIncomeRecordsFailed ||
      isFetchingApplicantAllVehicleRecordsFailed ||
      isFetchingApplicantAllPetRecordsFailed ||
      isFetchingOrganizationApplicationTCsFailed ||
      isGettingApplicationAcceptedTCsFailed
    ) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the application submission information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingAllRentalHistoryRecordsFailed,
    isFetchingAllRentalHistoryRecordsSuccess,
    isFetchingEmploymentIncomeRecordsFailed,
    isFetchingEmploymentIncomeRecordsSuccess,
    isFetchingApplicantAllPetRecordsFailed,
    isFetchingApplicantAllPetRecordsSuccess,
    isFetchingApplicantAllVehicleRecordsFailed,
    isFetchingApplicantAllVehicleRecordsSuccess,
    isFetchingOrganizationApplicationTCsSuccess,
    isFetchingOrganizationApplicationTCsFailed,
    isGettingApplicationAcceptedTCsFailed,
    isGettingApplicationAcceptedTCsSuccess,
  ]);
  //handle Submit

  const handleSubmit = () => {
    setSubmitButtonClicked(true);
    /**
     * if its the primary we update the applicant status right away
     * For coapplicants we need to show the modal first
     */
    if (isCurrentUserThePrimaryApplicant) {
      dispatch(
        updateApplicantStatus({
          applicationUuid: Application?.Uuid,
          applicantUuid: Applicant?.Uuid,
          updateApplicantStatusDto: { Status: UpdateApplicantStatusEnum.Completed } as UpdateApplicantStatusDto,
        }),
      );
    } else {
      setIsCoapplicantConfirmModalOpen(true);
    }
  };

  //Start: app pay modal
  const setSelectedPayHandler = (feesCents: number, lastFour: string, uuid: string) => {
    setPaymentFees(feesCents);
    setLast4(lastFour);
    setPayMethodUuid(uuid);
  };

  const handleConfirmAppPayModal = () => {
    setIsOpenAppPayModal(false);
    setGlobalError(null);
    setIsOpenAppReviewModal(true);
  };

  const getAvailablePayMethods = () => {
    const returnArray: PaymentTypeEnum[] = [];
    const ApplicationsConfigs = propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs;

    if (ApplicationsConfigs?.ApplicationsAchAccepted) returnArray.push(PaymentTypeEnum.Ach);
    if (ApplicationsConfigs?.ApplicationsCcAccepted) returnArray.push(PaymentTypeEnum.Cc);
    if (ApplicationsConfigs?.ApplicationsDbAccepted) returnArray.push(PaymentTypeEnum.Db);

    return returnArray;
  };

  const handleConfirmAppReviewModal = () => {
    setIsOpenAppReviewModal(false);
    setIsOpenAppConfirmModal(true);
  };

  const handleConfirmAppConfirmModal = () => {
    setIsOpenAppConfirmModal(false);
    setIsSpinnerModalOpen(true);

    const OnetimePayrequest = {
      TotalAmount: moneyToSubmit + paymentFees,
      FeeAmount: paymentFees,
      FeePayMethodUuid: payMethodUuid,
      PayerUuid: Applicant?.Uuid, //can also be ipaUser
      Memo: Application?.PropertyAddress ? `Application Fee: ${Application.PropertyAddress}` : 'Application Fee',
      TransactionDetails: [
        {
          Amount: moneyToSubmit,
          PropertyPaymentTypeId: propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsEntityPaymentTypeId,
          FromPayMethodUuid: payMethodUuid,
        },
      ],
      ApplicationUuid: Application?.Uuid,
    } as CreateOneTimePaymentDto;

    //if it's adhoc flow (ie, Pauly paying on behalf of Apollo)
    if (payMethodUuid === DEFAULT_ADHOC_CARD_UUID) {
      OnetimePayrequest.BankCard = adhocBankCard;
      OnetimePayrequest.FeePayMethodUuid = '';
      OnetimePayrequest.TransactionDetails = OnetimePayrequest.TransactionDetails.map((td) => ({ ...td, FromPayMethodUuid: undefined } as CreateTransactionDetailDto));
    } else if (payMethodUuid === DEFAULT_ADHOC_BANK_UUID) {
      OnetimePayrequest.BankAccount = adhocBankAccount;
      OnetimePayrequest.FeePayMethodUuid = '';
      OnetimePayrequest.TransactionDetails = OnetimePayrequest.TransactionDetails.map((td) => ({ ...td, FromPayMethodUuid: undefined } as CreateTransactionDetailDto));
    }
    dispatch(makeOneTimePayment(OnetimePayrequest));
  };

  const handleCloseSpinnerModal = useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  const handleCoapplicantSubmit = () => {
    setIsSpinnerModalOpen(true);
    setIsCoapplicantConfirmModalOpen(false);
    dispatch(
      updateApplicantStatus({
        applicationUuid: Application?.Uuid,
        applicantUuid: Applicant?.Uuid,
        updateApplicantStatusDto: { Status: UpdateApplicantStatusEnum.Completed } as UpdateApplicantStatusDto,
      }),
    );
  };

  const handleConfirmAppSubmissionWithoutPayment = () => {
    dispatch({
      type: changeApplicationStatus.type,
      payload: {
        applicationUuid: Application?.Uuid,
        changeAppStatusDto: {
          Status: ChangeApplicationStatusEnum.Submit,
        },
      },
    });

    setIsSpinnerModalOpen(true);
  };

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 4, minHeight: 'inherit' }}>
          <Grid container spacing={2}>
            {localError && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                <Typography className={classes.globalText}>{localError}</Typography>
              </Grid>
            )}
            <Grid item xs={12} md={5} lg={4} xl={3} sx={{ borderRight: { xs: 'none', md: '2px solid #F1F0F5' }, borderBottom: { xs: '2px solid #F1F0F5', md: 'none' }, pb: 3 }}>
              {!isSubmittable && (
                <Typography
                  style={{
                    fontSize: '14px',
                    lineHeight: '18px',
                  }}
                >
                  Click each section to review incomplete data and errors
                </Typography>
              )}
              {/* property info status */}
              <MuiLink
                style={{ cursor: 'pointer' }}
                underline="hover"
                color={'black'}
                onClick={(e) => {
                  history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.PropertyInformation.toLowerCase()}?validation=true`);
                }}
              >
                <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                  {propertyInfoCompleted ? (
                    <img
                      style={{
                        width: '12px',
                        height: '9px',
                        marginRight: '10px',
                      }}
                      src={CheckMarkIcon}
                      alt="Completed"
                    />
                  ) : (
                    <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                  )}
                  Property Information
                </Typography>
              </MuiLink>
              {!propertyInfoCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
              {/* personal info status */}
              <MuiLink
                style={{ cursor: 'pointer' }}
                underline="hover"
                color={'black'}
                onClick={(e) => {
                  history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.PersonalDetails.toLowerCase()}?validation=true`);
                }}
              >
                <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                  {personalInfoCompleted ? (
                    <img
                      style={{
                        width: '12px',
                        height: '9px',
                        marginRight: '10px',
                      }}
                      src={CheckMarkIcon}
                      alt="Completed"
                    />
                  ) : (
                    <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                  )}
                  Personal Info
                </Typography>
              </MuiLink>
              {!personalInfoCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
              {/* employment status */}
              <MuiLink
                style={{ cursor: 'pointer' }}
                underline="hover"
                color={'black'}
                onClick={(e) => {
                  history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.IncomeEmployment.toLowerCase()}?validation=true`);
                }}
              >
                <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                  {employmentCompleted ? (
                    <img
                      style={{
                        width: '12px',
                        height: '9px',
                        marginRight: '10px',
                      }}
                      src={CheckMarkIcon}
                      alt="Completed"
                    />
                  ) : (
                    <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                  )}
                  Employment
                </Typography>
              </MuiLink>
              {!employmentCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
              {/* rental history status */}
              <MuiLink
                style={{ cursor: 'pointer' }}
                underline="hover"
                color={'black'}
                onClick={(e) => {
                  history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.ResidentiallHistory.toLowerCase()}?validation=true`);
                }}
              >
                <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                  {rentalHistoryCompleted ? (
                    <img
                      style={{
                        width: '12px',
                        height: '9px',
                        marginRight: '10px',
                      }}
                      src={CheckMarkIcon}
                      alt="Completed"
                    />
                  ) : (
                    <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                  )}
                  Rental History
                </Typography>
              </MuiLink>
              {!rentalHistoryCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
              {/* co-applicant status */}
              {isCurrentUserThePrimaryApplicant && (
                <>
                  <MuiLink
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                    color={'black'}
                    onClick={(e) => {
                      history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.CosignersCooccupants.toLowerCase()}?validation=true`);
                    }}
                  >
                    <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                      {coapplicantCompleted ? (
                        <img
                          style={{
                            width: '12px',
                            height: '9px',
                            marginRight: '10px',
                          }}
                          src={CheckMarkIcon}
                          alt="Completed"
                        />
                      ) : (
                        <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                      )}
                      Coapplicants
                    </Typography>
                  </MuiLink>
                  {!coapplicantCompleted && (
                    <Typography
                      sx={{
                        fontFamily: 'Work Sans',
                        fontSize: '12px',
                        lineHeight: '22px',
                        color: '#DB3939',
                        ml: 4,
                        mb: -1,
                      }}
                    >
                      Waiting on Coapplicant
                    </Typography>
                  )}
                </>
              )}

              {/* minor status */}
              {showMinor && (
                <>
                  <MuiLink
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                    color={'black'}
                    onClick={(e) => {
                      history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.CosignersCooccupants.toLowerCase()}?validation=true`);
                    }}
                  >
                    <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                      {minorsCompleted ? (
                        <img
                          style={{
                            width: '12px',
                            height: '9px',
                            marginRight: '10px',
                          }}
                          src={CheckMarkIcon}
                          alt="Completed"
                        />
                      ) : (
                        <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                      )}
                      Non-Adult Occupants
                    </Typography>
                  </MuiLink>
                  {!minorsCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
                </>
              )}
              {/* pet status */}
              {showPet && (
                <>
                  <MuiLink
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                    color={'black'}
                    onClick={(e) => {
                      history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.PetsVehicles.toLowerCase()}?validation=true`);
                    }}
                  >
                    <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                      {petCompleted ? (
                        <img
                          style={{
                            width: '12px',
                            height: '9px',
                            marginRight: '10px',
                          }}
                          src={CheckMarkIcon}
                          alt="Completed"
                        />
                      ) : (
                        <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                      )}
                      Animals
                    </Typography>
                  </MuiLink>
                  {!petCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
                </>
              )}

              {/* vehicle status */}
              {showVehicle && (
                <>
                  <MuiLink
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                    color={'black'}
                    onClick={(e) => {
                      history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.PetsVehicles.toLowerCase()}?validation=true`);
                    }}
                  >
                    <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                      {vehicleCompleted ? (
                        <img
                          style={{
                            width: '12px',
                            height: '9px',
                            marginRight: '10px',
                          }}
                          src={CheckMarkIcon}
                          alt="Completed"
                        />
                      ) : (
                        <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                      )}
                      Vehicles
                    </Typography>
                  </MuiLink>
                  {!vehicleCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
                </>
              )}

              {/* additional question status */}
              {showAdditionalQuestions && (
                <>
                  <MuiLink
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                    color={'black'}
                    onClick={(e) => {
                      history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.AdditionalQuestions.toLowerCase()}?validation=true`);
                    }}
                  >
                    <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                      {additionalQuestionCompleted ? (
                        <img
                          style={{
                            width: '12px',
                            height: '9px',
                            marginRight: '10px',
                          }}
                          src={CheckMarkIcon}
                          alt="Completed"
                        />
                      ) : (
                        <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                      )}
                      Additional Questions
                    </Typography>
                  </MuiLink>
                  {!additionalQuestionCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
                </>
              )}
              {/* releases consents status */}
              <MuiLink
                style={{ cursor: 'pointer' }}
                underline="hover"
                color={'black'}
                onClick={(e) => {
                  history.push(`/resui/applications/${Application.Uuid}/${WizardPageStates.ReleasesConsents.toLowerCase()}?validation=true`);
                }}
              >
                <Typography className={classes.cardBodyText} sx={{ mt: 2 }}>
                  {consentsCompleted ? (
                    <img
                      style={{
                        width: '12px',
                        height: '9px',
                        marginRight: '10px',
                      }}
                      src={CheckMarkIcon}
                      alt="Completed"
                    />
                  ) : (
                    <img className={classes.incompleteImage} src={IncompleteIcon} alt="Incomplete" />
                  )}
                  Releases & Consents
                </Typography>
              </MuiLink>
              {!consentsCompleted && <Typography className={classes.incompleteLabelText}>Incomplete</Typography>}
            </Grid>

            <Grid item xs={12} md={6}>
              {!isSubmittable && Application?.Status?.toLowerCase() !== 'submitted' && (
                <Typography className={classes.bodyText} sx={{ mx: 2, mt: 2 }}>
                  Finish the incomplete sections of the application in order to submit.
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4 }}>
                {isCurrentUserThePrimaryApplicant && (
                  <>
                    <Typography className={classes.labelText}>Total Application Fee</Typography>
                    <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Typography className={classes.bodyText}>
                        {Application.ApplicationFee?.Adults.Count || 0} {`x Adults (at ${formatAsCurrency(Application.ApplicationFee?.Adults.Rate || 0)}/ Adult)`}
                      </Typography>
                      <Typography className={classes.bodyText}>{formatAsCurrency((Application.ApplicationFee?.Adults.Rate || 0) * (Application.ApplicationFee?.Adults.Count || 0))}</Typography>
                    </Box>
                    {!!Application.ApplicationFee?.Cosigners.Count && (
                      <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                        <Typography className={classes.bodyText}>
                          {Application.ApplicationFee?.Cosigners.Count || 0} {`x Cosigners (at ${formatAsCurrency(Application.ApplicationFee?.Cosigners.Rate || 0)}/ Cosigner)`}
                        </Typography>
                        <Typography className={classes.bodyText}>{formatAsCurrency((Application.ApplicationFee?.Cosigners.Rate || 0) * (Application.ApplicationFee?.Cosigners.Count || 0))}</Typography>
                      </Box>
                    )}
                    {!!Application.ApplicationFee?.Pets.Count && (
                      <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                        <Typography className={classes.bodyText}>
                          {Application.ApplicationFee?.Pets.Count || 0} {`x Pets (at ${formatAsCurrency(Application.ApplicationFee?.Pets.Rate || 0)}/ Pet)`}
                        </Typography>
                        <Typography className={classes.bodyText}>{formatAsCurrency((Application.ApplicationFee?.Pets.Rate || 0) * (Application.ApplicationFee?.Pets.Count || 0))}</Typography>
                      </Box>
                    )}
                    <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                      <Typography className={classes.bodyText}>Subtotal</Typography>
                      <Typography className={classes.bodyText}>
                        {formatAsCurrency(
                          (Application.ApplicationFee?.Pets.Rate || 0) * (Application.ApplicationFee?.Pets.Count || 0) +
                            (Application.ApplicationFee?.Adults.Rate || 0) * (Application.ApplicationFee?.Adults.Count || 0) +
                            (Application.ApplicationFee?.Cosigners.Rate || 0) * (Application.ApplicationFee?.Cosigners.Count || 0),
                        )}
                      </Typography>
                    </Box>
                    {alreadyPaidAmount > 0 && (
                      <>
                        <Box sx={{ maxWidth: 380, width: '100%', mt: 2, borderTop: '1px solid #000' }}></Box>
                        <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                          <Typography className={classes.bodyText}>Total Paid</Typography>
                          <Typography className={classes.bodyText}>-{formatAsCurrency(alreadyPaidAmount ?? 0)}</Typography>
                        </Box>
                      </>
                    )}
                    <Box sx={{ maxWidth: 380, width: '100%', mt: 2, borderTop: '1px solid #000' }}></Box>
                    <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', justifyContent: 'space-between', mt: 2.5 }}>
                      <Typography className={classes.bodyTextBold}>Grand Total</Typography>
                      <Typography className={classes.labelText}>{formatAsCurrency(moneyToSubmit ?? 0)}</Typography>
                    </Box>
                  </>
                )}
                <Box className={classes.wrapper} sx={{ mt: 2 }}>
                  <Button
                    type="button"
                    className={classes.wizardNextBtn}
                    sx={{
                      borderColor: isSubmittable ? '#39A2DB !important' : '#A8A8A8 !important',
                    }}
                    disabled={!isSubmittable || Application?.Status?.toLowerCase() === 'submitted'}
                    onClick={handleSubmit}
                  >
                    <Typography
                      className={classes.wizardNextBtnText}
                      sx={{
                        margin: '0 10px',
                        color: isSubmittable ? '#39A2DB' : '#A8A8A8',
                      }}
                    >
                      {Application?.Status?.toLowerCase() === 'submitted' ? 'Submitted' : isCurrentUserThePrimaryApplicant && moneyToSubmit !== 0 ? 'Submit & Pay' : 'Submit'}
                    </Typography>
                  </Button>
                </Box>
                <Box sx={{ maxWidth: 380, width: '100%', display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Typography variant="body2" gutterBottom id="terms-and-condition">
                    By Clicking Submit you agree to our{' '}
                    <span
                      className={classes.termsAndConditionLink}
                      onClick={() => {
                        window.open('/terms');
                      }}
                    >
                      {'T&Cs...'}
                    </span>
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      {isOpenAppPayModal && (
        <ApplicationPaymentModal
          open={isOpenAppPayModal}
          confirmCallback={handleConfirmAppPayModal}
          closeCallback={() => setIsOpenAppPayModal(false)}
          appFee={moneyToSubmit}
          payMethodUuid={payMethodUuid}
          setPayMethodUuid={setPayMethodUuid}
          setSelectedPayHandler={setSelectedPayHandler}
          availablePaymentMethods={getAvailablePayMethods()}
        />
      )}
      {isOpenAppReviewModal && (
        <ApplicationReviewModal
          open={isOpenAppReviewModal}
          confirmCallback={handleConfirmAppReviewModal}
          closeCallback={() => {
            setGlobalError(null);
            setIsOpenAppReviewModal(false);
          }}
          backCallback={() => {
            setIsOpenAppReviewModal(false);
            setIsOpenAppPayModal(true);
          }}
          globalError={globalError ?? undefined}
          companyName={Application?.OrganizationName ?? ''}
          propertyName={Application?.PropertyAddress ?? ''}
          applicantName={`${Applicant.FirstName} ${Applicant.LastName}`}
          paidAmount={moneyToSubmit}
          totalAmount={moneyToSubmit + paymentFees}
          transactionDate={format(new Date(), 'MM/dd/yy')}
          paymentFees={paymentFees}
          last4={last4}
        />
      )}
      {isOpenAppConfirmModal && <ConfirmPayModal open={isOpenAppConfirmModal} confirmCallback={handleConfirmAppConfirmModal} closeCallback={() => setIsOpenAppConfirmModal(false)} />}
      {isOpenAppPaymentSuccessModal && transaction && (
        <ApplicationPaymentSuccessModal
          open={isOpenAppPaymentSuccessModal}
          closeCallback={() => {
            setIsOpenAppPaymentSuccessModal(false);
            dispatch(
              //! is this necessary? won't the other page fetch it on load?
              getFetchingApplicationsData({
                RangeSize: 100,
                StartIndex: 0,
              }),
            );
            dispatch({ type: clearPayModalReduxStates.type }); //clear out the payment states for this app
            dispatch({ type: resetApplicationStatusChangeStates.type }); //clear out the app status change states for this app
            dispatch(getTransactionHistory());
            history.push(`/resui/applications`);
          }}
          appUuid={Application?.Uuid ?? ''}
          applicantUuid={Applicant.Uuid}
          paymentID={transaction?.TransactionUuid ?? ''}
          companyName={Application?.OrganizationName ?? ''}
          propertyName={Application?.PropertyAddress ?? ''}
          applicantName={`${Applicant.FirstName} ${Applicant.LastName}`}
          paidAmount={
            transaction?.TransactionDetails?.map((transactionDetail) => {
              if (transactionDetail.EntityPaymentTypeId === 1) return 0;
              return transactionDetail.Amount;
            }).reduce((prev, nex) => prev + nex, 0) ?? 0
          }
          totalAmount={transaction?.Amount ?? 0}
          transactionDate={transaction?.TransactionDate ?? ''}
          paymentFees={transaction?.TransactionDetails?.filter((transactionDetail) => transactionDetail.EntityPaymentTypeId === 1)?.[0]?.Amount ?? 0}
          last4={transaction?.TransactionDetails?.[0]?.FromPayMethod?.AccountLastFour ?? ''}
        />
      )}
      {isCoapplicantConfirmModalOpen && (
        <CoapplicantSubmitModal
          open={isCoapplicantConfirmModalOpen}
          cancelCallback={() => {
            setIsCoapplicantConfirmModalOpen(false);
          }}
          confirmCallback={handleCoapplicantSubmit}
        />
      )}
      {/**This is the confirmation modal for the flow when an app is returned and there has been no changes that would result in an increase in fees
       (i.e no payment required to submit the app) */}
      {isOpenAppConfirmSubModal && (
        <ConfirmAppSubmissionModal
          open={isOpenAppConfirmSubModal}
          closeCallback={() => {
            setIsOpenAppConfirmSubModal(false);
            setGlobalError(null);
          }}
          confirmCallback={handleConfirmAppSubmissionWithoutPayment}
          globalError={globalError ?? undefined}
        />
      )}
    </Box>
  );
}
